import React, { useState, useEffect } from 'react';
import {
  Check,
  Zap,
  ArrowRight,
  Smartphone,
  CheckCircle2,
  MessageCircle,
  Clock,
  Mail
} from 'lucide-react';
import { ViewTab } from '../types';
import { PublicHeader } from '../components/layout';
import { WhatsAppIcon } from '../components/ui/WhatsAppIcon';
import { PLANS_CONFIG } from '../config/plans.config';
import { PaymentModal } from '../components/PaymentModal';
import { useAuth } from '../auth/AuthContext';
import { api } from '../services/api';

const PLAN_MESSAGES_MAP: Record<string, number> = {
  free: PLANS_CONFIG.free.limits.quotaMessagesMonthly,
  starter: PLANS_CONFIG.starter.limits.quotaMessagesMonthly,
  pro: PLANS_CONFIG.pro.limits.quotaMessagesMonthly,
  business: PLANS_CONFIG.business.limits.quotaMessagesMonthly,
  agency: PLANS_CONFIG.agency.limits.quotaMessagesMonthly
};

const PLAN_RANK: Record<string, number> = {
  free: 0,
  starter: 1,
  pro: 2,
  business: 3,
  agency: 4
};

interface PricingViewProps {
  onNavigate: (tab: ViewTab) => void;
  onBack?: () => void;
  onSelectPlan?: (planId: string) => void;
}

export const PricingView: React.FC<PricingViewProps> = ({ onNavigate, onBack, onSelectPlan }) => {
  const { user, isAuthenticated } = useAuth();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [selectedSuccessPlan, setSelectedSuccessPlan] = useState<string | null>(null);
  const [currentQuota, setCurrentQuota] = useState<{
    planId: string;
    remaining: number;
    isPaidActive: boolean;
    isExpired?: boolean;
  } | null>(null);

  // Détecter un éventuel retour avec orderRef sur /pricing et basculer vers la facturation
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('orderRef')) {
      onNavigate('billing');
    }
  }, [onNavigate]);

  // Charger le quota actuel de l'utilisateur pour afficher le report bienveillant de ses messages
  useEffect(() => {
    if (!isAuthenticated) return;
    let isMounted = true;
    api.billing.getQuota().then((res) => {
      if (isMounted && res.success && res.data) {
        const isPaidActive = res.data.plan_id !== 'free' && !res.data.is_expired && res.data.remaining > 0;
        setCurrentQuota({
          planId: res.data.plan_id,
          remaining: res.data.remaining,
          isPaidActive,
          isExpired: Boolean(res.data.is_expired)
        });
      } else if (isMounted) {
        setCurrentQuota({
          planId: 'free',
          remaining: 0,
          isPaidActive: false
        });
      }
    }).catch(() => {
      if (isMounted) {
        setCurrentQuota({
          planId: 'free',
          remaining: 0,
          isPaidActive: false
        });
      }
    });
    return () => { isMounted = false; };
  }, [isAuthenticated]);

  // S'assurer que le scroll est en haut dès l'arrivée pour garder le header parfaitement transparent
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const mainEl = document.querySelector('main');
    if (mainEl) mainEl.scrollTop = 0;
  }, []);

  const isAnnual = billingCycle === 'annual';

  const formatPrice = (monthlyXof: number, isAnnualCycle: boolean) => {
    if (monthlyXof === 0) return '0';
    const price = isAnnualCycle ? Math.round(monthlyXof * 0.8) : monthlyXof;
    return price.toLocaleString('fr-FR');
  };

  // Plans simplifiés, formulés en langage clair pour un commerçant ou chef d'entreprise lambda (Branchés SSOT sur PLANS_CONFIG)
  const plans = [
    {
      id: 'free',
      name: PLANS_CONFIG.free.name,
      description: 'Automatisez vos échanges WhatsApp sans frais avec votre assistant IA.',
      monthlyXof: 0,
      badge: 'Gratuit à vie',
      highlight: false,
      tagline: 'Essai & découverte sans engagement',
      features: [
        `${PLANS_CONFIG.free.limits.maxWhatsappAccounts} numéro WhatsApp connecté`,
        `Jusqu’à ${PLANS_CONFIG.free.limits.quotaMessagesMonthly.toLocaleString('fr-FR')} réponses IA / mois`,
        `Jusqu’à ${PLANS_CONFIG.free.limits.maxKnowledgeDocs} fiches d’offres et tarifs`,
        'Réponses 24h/24 & reprise en main humaine'
      ],
      cta: 'Démarrer gratuitement'
    },
    {
      id: 'starter',
      name: PLANS_CONFIG.starter.name,
      description: PLANS_CONFIG.starter.description,
      monthlyXof: PLANS_CONFIG.starter.pricing.monthlyXof,
      badge: null,
      highlight: false,
      tagline: 'Pour indépendants & petites activités',
      features: [
        `${PLANS_CONFIG.starter.limits.maxWhatsappAccounts} numéro WhatsApp connecté`,
        `Jusqu’à ${PLANS_CONFIG.starter.limits.quotaMessagesMonthly.toLocaleString('fr-FR')} réponses IA / mois`,
        'Liens de paiement Wave, OM & RDV auto',
        `Jusqu’à ${PLANS_CONFIG.starter.limits.maxKnowledgeDocs} fiches d’offres et tarifs`,
        'Sans signature publicitaire • Support WhatsApp'
      ],
      cta: 'Choisir Starter'
    },
    {
      id: 'pro',
      name: PLANS_CONFIG.pro.name,
      description: PLANS_CONFIG.pro.description,
      monthlyXof: PLANS_CONFIG.pro.pricing.monthlyXof,
      badge: PLANS_CONFIG.pro.badge || 'Le plus populaire',
      highlight: true,
      tagline: 'Activité régulière & gestion quotidienne',
      features: [
        `${PLANS_CONFIG.pro.limits.maxWhatsappAccounts} numéro WhatsApp connecté`,
        `Jusqu’à ${PLANS_CONFIG.pro.limits.quotaMessagesMonthly.toLocaleString('fr-FR')} réponses IA / mois`,
        'Compréhension des notes vocales (30s)',
        `Paiements, RDV & ${PLANS_CONFIG.pro.limits.maxTeamMembers} accès collaborateurs`,
        `Jusqu’à ${PLANS_CONFIG.pro.limits.maxKnowledgeDocs} fiches • Support 7j/7`
      ],
      cta: 'Choisir Pro'
    },
    {
      id: 'business',
      name: PLANS_CONFIG.business.name,
      description: PLANS_CONFIG.business.description,
      monthlyXof: PLANS_CONFIG.business.pricing.monthlyXof,
      badge: 'Multi-lignes & Équipe',
      highlight: false,
      tagline: 'Équipes & multi-numéros',
      features: [
        `${PLANS_CONFIG.business.limits.maxWhatsappAccounts} numéros WhatsApp connectés inclus`,
        `Jusqu’à ${PLANS_CONFIG.business.limits.quotaMessagesMonthly.toLocaleString('fr-FR')} réponses IA / mois`,
        `Notes vocales (1 min) & ${PLANS_CONFIG.business.limits.maxTeamMembers} accès collaborateurs`,
        `Campagnes groupées (${PLANS_CONFIG.business.limits.maxCampaignRecipients.toLocaleString('fr-FR')} contacts)`,
        '100% Marque blanche • Support VIP dédié'
      ],
      cta: 'Passer à Business'
    },
    {
      id: 'agency',
      name: PLANS_CONFIG.agency.name,
      description: PLANS_CONFIG.agency.description,
      monthlyXof: PLANS_CONFIG.agency.pricing.monthlyXof,
      badge: PLANS_CONFIG.agency.badge || 'Grands volumes',
      highlight: false,
      tagline: 'Réseaux, franchises & multi-activités',
      features: [
        `${PLANS_CONFIG.agency.limits.maxWhatsappAccounts} numéros WhatsApp inclus (extensibles)`,
        `Jusqu’à ${PLANS_CONFIG.agency.limits.quotaMessagesMonthly.toLocaleString('fr-FR')} réponses IA / mois`,
        'Notes vocales (1 min) & accès équipe illimités',
        `Campagnes groupées (${PLANS_CONFIG.agency.limits.maxCampaignRecipients.toLocaleString('fr-FR')} contacts)`,
        '100% Marque blanche • Manager dédié'
      ],
      cta: 'Choisir Agence'
    }
  ];

  const [checkoutPlan, setCheckoutPlan] = useState<{
    id: string;
    name: string;
    amountXof: number;
  } | null>(null);

  // Si l'utilisateur arrive après connexion avec une intention d'achat mémorisée, ouvrir directement le paiement
  useEffect(() => {
    if (isAuthenticated && user && currentQuota !== null) {
      try {
        const pendingStr = sessionStorage.getItem('pending_checkout_plan');
        if (pendingStr) {
          const pending = JSON.parse(pendingStr);
          if (pending && pending.planId) {
            sessionStorage.removeItem('pending_checkout_plan');
            // Si c'est déjà son forfait en cours, on ne le propose pas comme choix de paiement
            if (currentQuota.planId === pending.planId && !currentQuota.isExpired) {
              return;
            }
            if (currentQuota.planId !== 'free' && !currentQuota.isExpired && (PLAN_RANK[pending.planId] ?? 0) < (PLAN_RANK[currentQuota.planId] ?? 0)) {
              return;
            }
            const cycle = pending.billingCycle || 'monthly';
            if (pending.billingCycle) setBillingCycle(cycle);
            const selected = plans.find(p => p.id === pending.planId);
            if (selected) {
              const calculatedPrice = cycle === 'annual' ? Math.round(selected.monthlyXof * 0.8) * 12 : selected.monthlyXof;
              setCheckoutPlan({
                id: selected.id,
                name: selected.name,
                amountXof: calculatedPrice
              });
            }
          }
        }
      } catch (e) {}
    }
  }, [isAuthenticated, user, currentQuota]);

  // Plan payant actif (non expiré) : les forfaits inférieurs sont déjà inclus, seul l'upgrade est proposé
  const hasActivePaidPlan = Boolean(currentQuota && currentQuota.planId !== 'free' && !currentQuota.isExpired);
  const isLowerThanCurrent = (planId: string) =>
    hasActivePaidPlan && (PLAN_RANK[planId] ?? 0) < (PLAN_RANK[currentQuota!.planId] ?? 0);
  const isActiveCurrentPlan = (planId: string) =>
    currentQuota?.planId === planId && (planId === 'free' || !currentQuota?.isExpired);
  const currentPlanName = currentQuota ? (plans.find(p => p.id === currentQuota.planId)?.name || currentQuota.planId) : '';

  const handlePlanClick = (planId: string) => {
    // Forfait déjà actif ou forfait inférieur (déjà inclus) : aucune action
    if (isActiveCurrentPlan(planId) || isLowerThanCurrent(planId)) {
      return;
    }

    // Si l'utilisateur n'est pas connecté, mémoriser son choix (si plan payant) et diriger vers l'inscription
    if (!isAuthenticated || !user) {
      try {
        if (planId === 'free') {
          sessionStorage.removeItem('pending_checkout_plan');
        } else {
          sessionStorage.setItem('pending_checkout_plan', JSON.stringify({ planId, billingCycle }));
        }
      } catch (_) {}
      onNavigate('register' as any);
      return;
    }

    const selected = plans.find(p => p.id === planId);
    if (!selected) return;

    if (planId === 'free') {
      if (onSelectPlan) onSelectPlan('free');
      setSelectedSuccessPlan('free');
      setTimeout(() => setSelectedSuccessPlan(null), 4500);
      return;
    }

    const calculatedPrice = isAnnual ? Math.round(selected.monthlyXof * 0.8) * 12 : selected.monthlyXof;
    setCheckoutPlan({
      id: selected.id,
      name: selected.name,
      amountXof: calculatedPrice
    });
  };

  return (
    <div className="w-full bg-white text-slate-900 antialiased min-h-screen relative overflow-x-hidden">

      {/* Header Public Réutilisable */}
      <PublicHeader onNavigate={onNavigate} activeTab="pricing" />

      <div className="space-y-10 sm:space-y-14 pt-20 sm:pt-24 md:pt-28 pb-16 sm:pb-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative z-10 animate-in fade-in duration-300">

        {/* 1. Titre & Commutateur de Facturation */}
        <div className="text-center space-y-3 max-w-2xl mx-auto px-2">
          <h1 className="font-serif text-3xl sm:text-5xl lg:text-6xl font-normal text-slate-950 tracking-[-0.02em] leading-tight">
            Des forfaits clairs, sans mauvaise surprise
          </h1>
          <p className="text-sm sm:text-base text-slate-600 leading-relaxed max-w-md sm:max-w-xl mx-auto font-normal">
            Choisissez la formule adaptée à votre activité. Activez votre assistant en 2 minutes, modifiez ou arrêtez à tout moment.
          </p>

          {/* Toggle Mensuel / Annuel (Design Pill épuré) */}
          <div className="flex items-center justify-center pt-2">
            <div className="bg-slate-100 p-1 rounded-full flex items-center border border-slate-200/80 shadow-2xs">
              <button
                type="button"
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-1.5 sm:py-2 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                  billingCycle === 'monthly'
                    ? 'bg-slate-900 text-white shadow-xs'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                Mensuel
              </button>
              <button
                type="button"
                onClick={() => setBillingCycle('annual')}
                className={`px-4 py-1.5 sm:py-2 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${
                  billingCycle === 'annual'
                    ? 'bg-slate-900 text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <span>Annuel</span>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold tracking-normal transition-colors ${
                  billingCycle === 'annual'
                    ? 'bg-emerald-500 text-white'
                    : 'bg-emerald-600/15 text-emerald-800'
                }`}>
                  -20%
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Alerte Notification Succès */}
        {selectedSuccessPlan && (
          <div className="max-w-xl mx-auto p-4 rounded-2xl bg-emerald-50 border border-emerald-500 text-emerald-800 flex items-center gap-3 animate-in fade-in zoom-in-95 shadow-sm">
            <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600" />
            <div className="text-xs sm:text-sm font-semibold">
              {selectedSuccessPlan === 'free'
                ? "Votre compte est configuré sur le forfait Free avec 50 réponses incluses chaque mois !"
                : `Demande d’activation enregistrée pour le plan ${selectedSuccessPlan.toUpperCase()} ! Notre équipe configure votre accès.`}
            </div>
          </div>
        )}

        {/* 2. Grille Équilibrée des Forfaits (Aérée, max 3 par rangée pour éviter la surcharge) */}
        <div className="space-y-6 max-w-6xl mx-auto">
          {/* Rangée 1 : Free, Starter, Pro */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 items-stretch">
            {plans.slice(0, 3).map((plan) => {
              const priceFormatted = formatPrice(plan.monthlyXof, isAnnual);
              const isCurrentPlan = isActiveCurrentPlan(plan.id);
              const isIncludedLowerPlan = isLowerThanCurrent(plan.id);
              const isRenewal = Boolean(currentQuota?.isExpired && currentQuota.planId === plan.id && plan.id !== 'free');
              const isUpgradeWithRollover = Boolean(
                currentQuota?.isPaidActive &&
                currentQuota.remaining > 0 &&
                (PLAN_RANK[plan.id] ?? 0) > (PLAN_RANK[currentQuota.planId] ?? 0)
              );
              const rolloverCount = isUpgradeWithRollover ? (currentQuota?.remaining || 0) : 0;
              const baseMsgs = PLAN_MESSAGES_MAP[plan.id] || 0;

              return (
                <div
                  key={plan.id}
                  className={`${isIncludedLowerPlan ? 'opacity-60 ' : ''}bg-white rounded-3xl p-6 sm:p-7 flex flex-col justify-between relative transition-all ${
                    isCurrentPlan
                      ? 'border-2 border-slate-900 shadow-md ring-2 ring-slate-900/10'
                      : plan.highlight
                        ? 'border-2 border-slate-900 shadow-lg'
                        : 'border border-slate-200/90 shadow-2xs hover:border-slate-300'
                  }`}
                >
                  <div className="space-y-4 relative z-10">
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-lg sm:text-xl font-bold tracking-tight text-slate-950 font-serif">
                          {plan.name}
                        </h3>
                        {isCurrentPlan ? (
                          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-900 text-white flex items-center gap-1 shadow-2xs">
                            <CheckCircle2 className="w-3 h-3" />
                            Actuel
                          </span>
                        ) : plan.highlight ? (
                          <span className="px-3 py-0.5 rounded-full text-[10px] font-semibold bg-slate-900 text-white shadow-2xs">
                            Recommandé
                          </span>
                        ) : plan.badge ? (
                          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200/60">
                            {plan.badge}
                          </span>
                        ) : null}
                      </div>
                      <p className="text-xs leading-relaxed min-h-[34px] text-slate-500">
                        {plan.description}
                      </p>
                    </div>

                    {/* Bloc Prix */}
                    <div className="pt-2 pb-3 border-b border-slate-100">
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-2xl sm:text-3xl font-black font-mono tracking-tight text-slate-950">
                          {priceFormatted}
                        </span>
                        <span className="text-xs font-bold text-slate-800">
                          FCFA
                        </span>
                        <span className="text-[11px] font-normal text-slate-500">
                          / mois
                        </span>
                      </div>
                      {plan.id === 'free' ? (
                        <div className="text-[11px] font-semibold text-slate-600 mt-1">
                          Gratuit pour toujours • Sans carte
                        </div>
                      ) : (isAnnual && plan.monthlyXof > 0) ? (
                        <div className="text-[11px] font-semibold text-slate-600 mt-1">
                          Économisez 20% avec le paiement annuel
                        </div>
                      ) : null}

                      {isCurrentPlan && (
                        <div className="mt-2.5 p-2 rounded-xl border border-slate-200 bg-slate-50 text-center text-xs font-bold text-slate-800 flex items-center justify-center gap-1.5">
                          <CheckCircle2 className="w-4 h-4 text-slate-700 shrink-0" />
                          <span>Votre forfait actuel ({currentQuota?.remaining.toLocaleString('fr-FR')} réponses restantes)</span>
                        </div>
                      )}

                      {isUpgradeWithRollover && (
                        <div className="mt-2.5 p-2 rounded-xl bg-slate-100 border border-slate-200 text-slate-800 text-[11px] font-semibold flex items-center gap-1.5">
                          <Zap className="w-3.5 h-3.5 text-slate-900 shrink-0" />
                          <span>+<strong>{rolloverCount.toLocaleString('fr-FR')} réponses</strong> restantes conservées et ajoutées !</span>
                        </div>
                      )}
                    </div>

                    {/* Ce qui est inclus */}
                    <div className="space-y-2.5 pt-1">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Ce qui est inclus :
                      </div>
                      <ul className="space-y-2 text-xs">
                        {plan.features.map((feat, idx) => {
                          let displayText = feat;
                          if (isUpgradeWithRollover && feat.includes('réponses') && baseMsgs > 0) {
                            displayText = `Jusqu’à ${baseMsgs.toLocaleString('fr-FR')} (+${rolloverCount.toLocaleString('fr-FR')} reportées = ${(baseMsgs + rolloverCount).toLocaleString('fr-FR')}) réponses / mois`;
                          }
                          return (
                            <li key={idx} className="flex items-start gap-2 leading-snug">
                              <Check className="w-3.5 h-3.5 text-slate-900 shrink-0 mt-0.5" />
                              <span className="text-slate-700 font-normal">
                                {displayText}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>

                  {/* Bouton d'action */}
                  <div className="pt-6 relative z-10">
                    {isCurrentPlan ? (
                      <button
                        type="button"
                        disabled
                        aria-disabled="true"
                        className="w-full py-2.5 px-4 rounded-full font-medium text-xs flex items-center justify-center gap-2 cursor-default select-none border border-slate-200 bg-slate-100 text-slate-500 shadow-none"
                      >
                        <CheckCircle2 className="w-4 h-4 text-slate-500 shrink-0" />
                        <span>Votre forfait actuel</span>
                      </button>
                    ) : isIncludedLowerPlan ? (
                      <button
                        type="button"
                        disabled
                        aria-disabled="true"
                        className="w-full py-2.5 px-4 rounded-full font-medium text-xs flex items-center justify-center gap-2 cursor-default select-none border bg-slate-50 text-slate-400 border-slate-200 shadow-none"
                      >
                        <Check className="w-4 h-4 text-slate-400 shrink-0" />
                        <span>Inclus dans votre forfait {currentPlanName}</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handlePlanClick(plan.id)}
                        className="w-full py-2.5 px-4 rounded-full font-semibold text-xs flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-xs active:scale-[0.99] bg-slate-900 hover:bg-black text-white"
                      >
                        <span>{isRenewal ? `Renouveler ${plan.name}` : (hasActivePaidPlan || isUpgradeWithRollover) ? `Passer à ${plan.name}` : plan.cta}</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Rangée 2 : Business & Agence (2 colonnes centrées sur PC) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 max-w-4xl mx-auto items-stretch">
            {plans.slice(3).map((plan) => {
              const priceFormatted = formatPrice(plan.monthlyXof, isAnnual);
              const isCurrentPlan = isActiveCurrentPlan(plan.id);
              const isIncludedLowerPlan = isLowerThanCurrent(plan.id);
              const isRenewal = Boolean(currentQuota?.isExpired && currentQuota.planId === plan.id && plan.id !== 'free');
              const isUpgradeWithRollover = Boolean(
                currentQuota?.isPaidActive &&
                currentQuota.remaining > 0 &&
                (PLAN_RANK[plan.id] ?? 0) > (PLAN_RANK[currentQuota.planId] ?? 0)
              );
              const rolloverCount = isUpgradeWithRollover ? (currentQuota?.remaining || 0) : 0;
              const baseMsgs = PLAN_MESSAGES_MAP[plan.id] || 0;

              return (
                <div
                  key={plan.id}
                  className={`${isIncludedLowerPlan ? 'opacity-60 ' : ''}bg-white rounded-3xl p-6 sm:p-7 flex flex-col justify-between relative transition-all border border-slate-200/90 shadow-2xs hover:border-slate-300`}
                >
                  <div className="space-y-4 relative z-10">
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-lg sm:text-xl font-bold tracking-tight text-slate-950 font-serif">
                          {plan.name}
                        </h3>
                        {isCurrentPlan ? (
                          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-900 text-white flex items-center gap-1 shadow-2xs">
                            <CheckCircle2 className="w-3 h-3" />
                            Actuel
                          </span>
                        ) : plan.badge ? (
                          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200/60">
                            {plan.badge}
                          </span>
                        ) : null}
                      </div>
                      <p className="text-xs leading-relaxed min-h-[34px] text-slate-500">
                        {plan.description}
                      </p>
                    </div>

                    {/* Bloc Prix */}
                    <div className="pt-2 pb-3 border-b border-slate-100">
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-2xl sm:text-3xl font-black font-mono tracking-tight text-slate-950">
                          {priceFormatted}
                        </span>
                        <span className="text-xs font-bold text-slate-800">
                          FCFA
                        </span>
                        <span className="text-[11px] font-normal text-slate-500">
                          / mois
                        </span>
                      </div>
                      {plan.id === 'free' ? (
                        <div className="text-[11px] font-semibold text-slate-600 mt-1">
                          Gratuit pour toujours • Sans carte
                        </div>
                      ) : (isAnnual && plan.monthlyXof > 0) ? (
                        <div className="text-[11px] font-semibold text-slate-600 mt-1">
                          Économisez 20% avec le paiement annuel
                        </div>
                      ) : null}

                      {isCurrentPlan && (
                        <div className="mt-2.5 p-2 rounded-xl border border-slate-200 bg-slate-50 text-center text-xs font-bold text-slate-800 flex items-center justify-center gap-1.5">
                          <CheckCircle2 className="w-4 h-4 text-slate-700 shrink-0" />
                          <span>Votre forfait actuel ({currentQuota?.remaining.toLocaleString('fr-FR')} réponses restantes)</span>
                        </div>
                      )}

                      {isUpgradeWithRollover && (
                        <div className="mt-2.5 p-2 rounded-xl bg-slate-100 border border-slate-200 text-slate-800 text-[11px] font-semibold flex items-center gap-1.5">
                          <Zap className="w-3.5 h-3.5 text-slate-900 shrink-0" />
                          <span>+<strong>{rolloverCount.toLocaleString('fr-FR')} réponses</strong> restantes conservées et ajoutées !</span>
                        </div>
                      )}
                    </div>

                    {/* Ce qui est inclus */}
                    <div className="space-y-2.5 pt-1">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Ce qui est inclus :
                      </div>
                      <ul className="space-y-2 text-xs">
                        {plan.features.map((feat, idx) => {
                          let displayText = feat;
                          if (isUpgradeWithRollover && feat.includes('réponses') && baseMsgs > 0) {
                            displayText = `Jusqu’à ${baseMsgs.toLocaleString('fr-FR')} (+${rolloverCount.toLocaleString('fr-FR')} reportées = ${(baseMsgs + rolloverCount).toLocaleString('fr-FR')}) réponses / mois`;
                          }
                          return (
                            <li key={idx} className="flex items-start gap-2 leading-snug">
                              <Check className="w-3.5 h-3.5 text-slate-900 shrink-0 mt-0.5" />
                              <span className="text-slate-700 font-normal">
                                {displayText}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>

                  {/* Bouton d'action */}
                  <div className="pt-6 relative z-10">
                    {isCurrentPlan ? (
                      <button
                        type="button"
                        disabled
                        aria-disabled="true"
                        className="w-full py-2.5 px-4 rounded-full font-medium text-xs flex items-center justify-center gap-2 cursor-default select-none border border-slate-200 bg-slate-100 text-slate-500 shadow-none"
                      >
                        <CheckCircle2 className="w-4 h-4 text-slate-500 shrink-0" />
                        <span>Votre forfait actuel</span>
                      </button>
                    ) : isIncludedLowerPlan ? (
                      <button
                        type="button"
                        disabled
                        aria-disabled="true"
                        className="w-full py-2.5 px-4 rounded-full font-medium text-xs flex items-center justify-center gap-2 cursor-default select-none border bg-slate-50 text-slate-400 border-slate-200 shadow-none"
                      >
                        <Check className="w-4 h-4 text-slate-400 shrink-0" />
                        <span>Inclus dans votre forfait {currentPlanName}</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handlePlanClick(plan.id)}
                        className="w-full py-2.5 px-4 rounded-full font-semibold text-xs flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-xs active:scale-[0.99] bg-slate-900 hover:bg-black text-white"
                      >
                        <span>{isRenewal ? `Renouveler ${plan.name}` : (hasActivePaidPlan || isUpgradeWithRollover) ? `Passer à ${plan.name}` : plan.cta}</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Précisions flat en gris épuré sans bloc ni bordure */}
        <div className="max-w-3xl mx-auto text-center space-y-1 text-[11px] sm:text-xs text-slate-400 font-normal pt-1">
          <p>
            Notes vocales : français, anglais et espagnol (30s max pour Pro, 1 min pour Business et Agence).
          </p>
          <p>
            Connexion WhatsApp : continue 24h/24 sur forfaits payants • Sur Free, mise en veille après 72h sans message (reconnexion en 1 clic).
          </p>
        </div>

        {/* 3. Blocs Bento Modulaires : Recharges de réponses & Plusieurs numéros */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
          
          {/* Bento Card A : Recharges de réponses (Direct & Sans Blabla) */}
          <div className="bg-slate-100/80 rounded-3xl p-4 sm:p-5 border-0 shadow-none flex flex-col justify-between space-y-3.5">
            <div className="space-y-3">
              {/* Header Bento */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white text-amber-500 flex items-center justify-center shrink-0 border-0 shadow-none">
                  <Zap className="w-5 h-5 stroke-[2.2] fill-amber-400" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-xs sm:text-sm font-bold text-slate-900 leading-tight">
                    Besoin de réponses supplémentaires ?
                  </h3>
                  <p className="text-[11px] sm:text-xs text-slate-500 truncate mt-0.5">
                    Ajoutez du volume en 1 clic sans changer d'abonnement.
                  </p>
                </div>
              </div>

              {/* Sous-bento blanc interne : Liste d'avantages & CTA */}
              <div className="bg-white rounded-xl p-3 sm:p-4 border-0 shadow-none space-y-3">
                <ul className="space-y-2 text-xs text-slate-600 font-normal">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Ajout de réponses disponible à tout moment en 1 clic</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Vos réponses achetées s'ajoutent instantanément</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Zéro expiration tant que votre abonnement reste actif</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Packs dès 1 900 F (+250, +500, +1 000, +2 500 rép.)</span>
                  </li>
                </ul>

                <button
                  type="button"
                  onClick={() => {
                    sessionStorage.setItem('recharge_initial_tab', 'messages');
                    onNavigate(isAuthenticated ? 'recharge' : ('login' as any));
                  }}
                  className="w-full bg-slate-900 hover:bg-black active:scale-[0.99] text-white text-xs font-bold py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-all cursor-pointer border-0 shadow-none mt-1"
                >
                  <span>Accéder aux recharges de réponses</span>
                  <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                </button>
              </div>
            </div>
          </div>

          {/* Bento Card B : Multi-lignes & Équipes (Direct, Universel & Sans Blabla) */}
          <div className="bg-slate-100/80 rounded-3xl p-4 sm:p-5 border-0 shadow-none flex flex-col justify-between space-y-3.5">
            <div className="space-y-3">
              {/* Header Bento */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white text-purple-600 flex items-center justify-center shrink-0 border-0 shadow-none">
                  <Smartphone className="w-5 h-5 text-purple-600 stroke-[2.2]" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-xs sm:text-sm font-bold text-slate-900 leading-tight">
                    Plusieurs numéros WhatsApp ?
                  </h3>
                  <p className="text-[11px] sm:text-xs text-slate-500 truncate mt-0.5">
                    Gérez tous vos numéros au même endroit.
                  </p>
                </div>
              </div>

              {/* Sous-bento blanc interne : Liste d'avantages & CTA */}
              <div className="bg-white rounded-xl p-3 sm:p-4 border-0 shadow-none space-y-3">
                <ul className="space-y-2 text-xs text-slate-600 font-normal">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>1 numéro distinct par service, collaborateur ou activité</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Offres, consignes et rendez-vous dédiés par numéro</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Valable pendant votre abonnement • Délai de grâce de 7 jours</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Packs dès 2 500 F par numéro supplémentaire (sur Business & Agence)</span>
                  </li>
                </ul>

                {currentQuota?.planId === 'business' || currentQuota?.planId === 'agency' ? (
                  <button
                    type="button"
                    onClick={() => {
                      sessionStorage.setItem('recharge_initial_tab', 'numbers');
                      onNavigate(isAuthenticated ? 'recharge' : ('login' as any));
                    }}
                    className="w-full bg-slate-900 hover:bg-black active:scale-[0.99] text-white text-xs font-bold py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 cursor-pointer transition-all border-0 shadow-none mt-1"
                  >
                    <span>Gérer & ajouter des numéros</span>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handlePlanClick('business')}
                    className="w-full bg-slate-900 hover:bg-black active:scale-[0.99] text-white text-xs font-bold py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 cursor-pointer transition-all border-0 shadow-none mt-1"
                  >
                    <span>Débloquer avec l'offre Business</span>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                )}
              </div>
            </div>
          </div>

        </div>

        {/* 4. Réassurance Flat & Élégante (sans bordure, épurée) */}
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-y-2.5 gap-x-8 text-[11px] sm:text-xs text-slate-500 py-2">
          <span className="inline-flex items-center gap-1.5 font-medium">
            <Check className="w-3.5 h-3.5 text-emerald-500 stroke-[2.5]" />
            Sans engagement
          </span>
          <span className="inline-flex items-center gap-1.5 font-medium">
            <Check className="w-3.5 h-3.5 text-emerald-500 stroke-[2.5]" />
            Paiements Wave, Orange Money & Carte
          </span>
          <span className="inline-flex items-center gap-1.5 font-medium">
            <Check className="w-3.5 h-3.5 text-emerald-500 stroke-[2.5]" />
            Votre numéro WhatsApp conservé
          </span>
        </div>

        {/* 5. Bannière Bas de Page / Contact Assistance (Style Metallic Waves & Minimal Mobile) */}
        <div className="max-w-5xl mx-auto bg-black text-white rounded-3xl p-6 sm:p-9 border border-white/10 shadow-2xl flex flex-col sm:flex-row items-center justify-between gap-5 text-center sm:text-left relative overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-right sm:bg-center opacity-70 pointer-events-none"
            style={{ backgroundImage: `url('/cta-waves-bg.webp')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-transparent pointer-events-none" />
          
          <div className="space-y-1.5 relative z-10 max-w-md">
            <h3 className="font-serif text-2xl sm:text-3xl font-normal tracking-[-0.02em]">
              Une hésitation avant de commencer ?
            </h3>
            <p className="text-xs sm:text-sm text-slate-300 font-normal leading-relaxed">
              Notre équipe vous conseille et vous aide à choisir la formule idéale pour votre activité, 7j/7.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2.5 relative z-10 w-full sm:w-auto">
            <a
              href={`https://wa.me/2290129647182?text=${encodeURIComponent("Bonjour l'équipe Zeyilo, j'ai une question sur les forfaits :")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-[#25D366] hover:bg-[#20ba59] active:scale-95 text-white font-medium text-xs shadow-xs transition-all cursor-pointer"
            >
              <WhatsAppIcon variant="transparent" size={14} className="text-white" />
              <span>Contacter sur WhatsApp</span>
            </a>

            <a
              href={`mailto:support@zeyilo.com?subject=${encodeURIComponent("[Zeyilo] Question sur les forfaits")}`}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/15 active:scale-95 text-white font-medium text-xs border border-white/20 transition-all cursor-pointer"
            >
              <Mail className="w-3.5 h-3.5 text-slate-300" />
              <span>support@zeyilo.com</span>
            </a>
          </div>
        </div>

      </div>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-slate-200/60 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-2.5">
            <img src="/logo.webp" alt="Zeyilo" width={120} height={36} loading="lazy" decoding="async" className="h-5 w-auto object-contain" />
            <span>— L'Agent IA WhatsApp des entreprises modernes</span>
          </div>
          <div className="flex items-center gap-5">
            <a href="/blog" className="hover:text-slate-900 transition-colors">
              Blog
            </a>
            <a href="/partenariat" className="hover:text-slate-900 transition-colors">
              Partenariat
            </a>
            <a href="/confidentialite" className="hover:text-slate-900 transition-colors">
              Confidentialité
            </a>
            <a href="/cgu" className="hover:text-slate-900 transition-colors">
              Conditions Générales
            </a>
            <button
              onClick={() => onNavigate('landing')}
              className="hover:text-slate-900 transition-colors cursor-pointer"
            >
              Accueil
            </button>
          </div>
        </div>
      </footer>

      {isAuthenticated && checkoutPlan && (
        <PaymentModal
          isOpen={!!checkoutPlan}
          onClose={() => setCheckoutPlan(null)}
          onNavigate={(tab) => {
            setCheckoutPlan(null);
            onNavigate(tab as any);
          }}
          type="plan"
          targetId={checkoutPlan.id}
          title={`Forfait ${checkoutPlan.name}`}
          amountXof={checkoutPlan.amountXof}
          billingCycle={billingCycle}
        />
      )}
    </div>
  );
};
